import lodash from 'lodash';
import type { RefObject } from 'react';
import React, { Component, Suspense, createRef, useEffect, useMemo } from 'react';

import { extractPluginsConfig } from './../wix-ricos-common/index.js';
import { resolveFileUrl } from './../ricos-media/index.js';
import { Version } from './../ricos-content/index.js';
import type { RichContent } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { resolveTheme } from './../ricos-styles/resolve-theme.js';
import type { BICallbacks, SEOSettings } from './../ricos-types/index.js';
import { useBiHelpers } from './../_shared/bi-strategy/use-bi-helpers.js';
import { useGetWixClientPromise } from './../_shared/request-service-provider/use-get-wix-client-promise.js';
import { WixInstanceContextProvider } from './../_shared/request-service-provider/wix-instance-context.js';
import { getImageUrl } from './../_shared/url-resolvers.js';
import { useIframeSandboxDomain } from './../_shared/use-iframe-sandbox-domain.js';
import { addDevToolsClickListener } from './../wix-rich-content-common/index.js';
import {
  RicosDevToolsLazy,
  shouldShowDebuggingInfo,
} from './../wix-rich-content-common/lib/devTools.js';
import { getContentSummary } from './../wix-rich-content-common/lib/ricosContentAnalytics.js';

import { ContentViewer } from './ContentViewer/index.js';
import { isViewerPlugin } from './is-viewer-plugin.js';
import type { RicosViewerProps } from './types.js';
import { mergeViewerProps } from './utils.js';

interface State {
  error?: string;
  TextSelectionToolbar?: $TSFixMe;
  LinkPreviewPopover?: $TSFixMe;
  debugInvokedWithMouse?: boolean;
  content?: RichContent;
}

class RicosViewerNext extends Component<RicosViewerProps, State> {
  viewerRef!: RefObject<any>;

  getBiCallback: <T extends keyof BICallbacks>(key: T) => BICallbacks[T] | undefined;

  static displayName = 'RicosViewerNext';

  static getDerivedStateFromError(error: string) {
    return { error };
  }

  componentDidCatch(error: $TSFixMe, errorInfo: $TSFixMe) {
    console.error({ error, errorInfo });
  }

  constructor(props: RicosViewerProps) {
    super(props);
    this.getBiCallback = key => {
      const { children, _rcProps } = this.props;
      return children?.props.helpers?.[key] || _rcProps?.helpers?.[key];
    };
    this.viewerRef = createRef();
    this.state = {};
  }

  static defaultProps = {
    onError: (err: $TSFixMe) => {
      throw err;
    },
  };

  componentDidMount() {
    addDevToolsClickListener(this.viewerRef.current, () =>
      this.setState({ debugInvokedWithMouse: true })
    );
  }

  loadTextSelection = () => {
    const { textSelectionToolbar, isMobile } = this.props;
    const needTextSelectionToolbar = textSelectionToolbar && !isMobile;
    if (needTextSelectionToolbar && !this.state.TextSelectionToolbar) {
      const TextSelectionToolbar = React.lazy(() => import('./TextSelectionToolbar.js'));
      this.setState({ TextSelectionToolbar });
    }
  };

  loadLinkPreviewPopover = () => {
    const { linkPreviewPopoverFetchData, isMobile } = this.props;
    const needLoadLinkPreviewPopover = linkPreviewPopoverFetchData && !isMobile;
    if (needLoadLinkPreviewPopover && !this.state.LinkPreviewPopover) {
      const LinkPreviewPopover = React.lazy(() => import('./LinkPreviewPopover.js'));
      this.setState({ LinkPreviewPopover });
    }
  };

  onMouseOver = () => {
    this.loadTextSelection();
    this.loadLinkPreviewPopover();
  };

  render() {
    const { seoSettings, linkPreviewPopoverFetchData, mediaSettings, content, debugMode, isPrint } =
      this.props;
    const { TextSelectionToolbar, LinkPreviewPopover } = this.state;
    const mergedProps = mergeViewerProps({
      ...this.props,
      viewerProps: {
        seoMode: seoSettings as SEOSettings | undefined,
        onMouseOver: this.onMouseOver,
        locale: this.props.locale,
        isPrint,
      },
    });

    try {
      if (this.state.error) {
        this.props.onError?.(this.state.error);
        return null;
      }
      return [
        <ContentViewer
          {...mergedProps}
          content={this.state.content ?? mergedProps.content}
          config={mergedProps.config}
          onHover={() => {}}
          key="viewer"
          setRef={this.viewerRef}
          fullscreenProps={mediaSettings?.fullscreenProps}
          devTools={
            this.state.debugInvokedWithMouse ||
            shouldShowDebuggingInfo(debugMode?.includes('all')) ? (
              <React.Suspense>
                <RicosDevToolsLazy
                  schema={{
                    getContent: () => (this.state.content ?? mergedProps.content) as RichContent,
                    setContent: content => this.setState({ content }),
                  }}
                  version={Version.currentVersion}
                  mode="viewer"
                  contentState={content}
                  ricosProps={this.props}
                  autoOpen={this.state.debugInvokedWithMouse ?? false}
                />
              </React.Suspense>
            ) : null
          }
        />,
        TextSelectionToolbar ? (
          <Suspense key="TextSelectionToolbar" fallback={<div />}>
            <TextSelectionToolbar
              onButtonClick={this.getBiCallback('onViewerAction')}
              container={this.viewerRef.current}
            />
          </Suspense>
        ) : null,
        LinkPreviewPopover ? (
          <Suspense key="LinkPreviewPopover" fallback={<div />}>
            <LinkPreviewPopover
              fetchUrlPreviewData={linkPreviewPopoverFetchData}
              container={this.viewerRef.current}
            />
          </Suspense>
        ) : null,
      ];
    } catch (e) {
      // @ts-ignore $TSFixMe
      this.props.onError?.(e);
      return null;
    }
  }
}

/**
 * Wrapper component that lazy loads BI helpers and passes them to the class component.
 * Waits for BI helpers to load before rendering the viewer to ensure onViewerLoaded
 * has access to the loaded helpers.
 */
const RicosViewerWithHelpers = (props: RicosViewerProps) => {
  const {
    biSettings,
    instance,
    _rcProps,
    iframeSandboxDomain: explicitDomain,
    theme,
    ...restProps
  } = props;

  // Lazy load BI helpers only when biSettings AND instance are provided
  const { helpers: biHelpers, biLoadPromise } = useBiHelpers({ instance, biSettings });

  // Fire onViewerLoaded once BI helpers finish loading
  useEffect(() => {
    biLoadPromise.then(callbacks => {
      const content = props.content || _rcProps?.content;
      const { pluginsCount } = (content && getContentSummary(content)) || {};
      if (pluginsCount) {
        callbacks.onViewerLoaded?.({
          isPreview: false,
          version: Version.currentVersion,
          pluginsCount,
          url: document.URL,
          contentId: content?.metadata?.id,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute iframeSandboxDomain from instance if not explicitly provided
  const iframeSandboxDomain = useIframeSandboxDomain(explicitDomain, instance);

  // Resolve Wix theme strategy (liveSite/wixStyle/nativeStyle)
  const resolvedTheme = useMemo(
    () => resolveTheme({ theme, isMobile: restProps.isMobile }),
    [theme, restProps.isMobile]
  );

  const pluginConfig = useMemo(
    () => extractPluginsConfig(props.plugins?.filter(isViewerPlugin)),
    [props.plugins]
  );

  const { getWixClientPromise } = useGetWixClientPromise(instance ?? '');

  const mergedRcProps = useMemo(
    () => ({
      ..._rcProps,
      config: lodash.merge({}, _rcProps?.config, pluginConfig.config),
      helpers: {
        ..._rcProps?.helpers,
        ...biHelpers,
        getImageUrl,
        resolveFileUrl:
          // Some urls might be resolved even without msid or instance, so we call it regardless
          resolveFileUrl(props.msid ?? '', props.instance ?? ''),
      },
    }),
    [_rcProps, biHelpers, props.msid, props.instance, pluginConfig]
  );

  return (
    <WixInstanceContextProvider
      getWixClientPromise={getWixClientPromise}
      instance={instance}
      jsdomMode={undefined}
    >
      <RicosViewerNext
        {...restProps}
        _rcProps={mergedRcProps}
        iframeSandboxDomain={iframeSandboxDomain}
        theme={resolvedTheme}
      />
    </WixInstanceContextProvider>
  );
};
RicosViewerWithHelpers.displayName = 'RicosViewer';

export default RicosViewerWithHelpers;
